/**
 * CR500: Canonical Renderer Eligibility Helper
 * 
 * Single source of truth for determining which deal card renderer (V1 or V2)
 * should be used across all admin preview surfaces.
 * 
 * This consolidates rollout logic from:
 * - v2Override.ts (query params)
 * - rolloutConfig.ts (percent/allowlist)
 * - useDealCardSpecV2.ts (feature flag)
 * 
 * Goals:
 * - When V2 rollout is ON (100%), force V2 everywhere
 * - When forced, disable manual toggle
 * - Provide clear reason for debugging
 */

import { useState, useEffect, useCallback, useMemo } from "react";
import { supabase } from "@/integrations/supabase/client";

// ================================================================
// TYPES
// ================================================================

export type RendererVersion = "v1" | "v2";
export type RendererReason = 
  | "forced_by_rollout"      // V2 is ON and percent >= 100
  | "forced_by_query_param"  // ?dealCardV2=1 or ?dealCardV2=0
  | "rollout_percent"        // Rollout is between 1-99%
  | "allowlist_match"        // User is in allowlist
  | "manual"                 // User selected manually (not forced)
  | "default";               // Fallback to V1

export interface RendererDecision {
  /** Which renderer to use */
  renderer: RendererVersion;
  /** Why this renderer was selected */
  reason: RendererReason;
  /** Whether the choice can be manually overridden */
  isForced: boolean;
  /** Debug snapshot of config keys */
  debugSnapshot?: {
    dealCardSpecV2_enabled?: boolean;
    deal_card_v2_rollout?: {
      enabled?: boolean;
      mode?: string;
      percent?: number;
    };
    queryOverride?: boolean | null;
  };
}

// ================================================================
// CONFIG LOADING (with caching)
// ================================================================

interface RolloutConfigCache {
  dealCardSpecV2_enabled: boolean;
  deal_card_v2_rollout: {
    enabled: boolean;
    mode: string;
    percent: number;
    allowlist: string[];
  } | null;
  timestamp: number;
}

let configCache: RolloutConfigCache | null = null;
const CACHE_TTL_MS = 30_000; // 30 seconds

/**
 * Fetch and cache the rollout configuration from app_config.
 */
async function fetchRolloutConfigFromDB(): Promise<RolloutConfigCache> {
  const now = Date.now();
  
  // Return cached if fresh
  if (configCache && now - configCache.timestamp < CACHE_TTL_MS) {
    return configCache;
  }
  
  try {
    // Fetch both config keys in parallel
    const [specEnabledResult, rolloutResult] = await Promise.all([
      supabase
        .from("app_config")
        .select("value_json")
        .eq("key", "dealCardSpecV2_enabled")
        .maybeSingle(),
      supabase
        .from("app_config")
        .select("value_json")
        .eq("key", "deal_card_v2_rollout")
        .maybeSingle(),
    ]);
    
    // Parse dealCardSpecV2_enabled (can be boolean or { enabled: boolean })
    let specEnabled = false;
    if (specEnabledResult.data?.value_json != null) {
      const val = specEnabledResult.data.value_json;
      if (typeof val === "boolean") {
        specEnabled = val;
      } else if (typeof val === "object" && val !== null && "enabled" in val) {
        specEnabled = Boolean((val as { enabled?: boolean }).enabled);
      } else {
        specEnabled = Boolean(val);
      }
    }
    
    // Parse deal_card_v2_rollout
    let rolloutConfig = null;
    if (rolloutResult.data?.value_json != null) {
      const val = rolloutResult.data.value_json as Record<string, unknown>;
      rolloutConfig = {
        enabled: Boolean(val.enabled),
        mode: String(val.mode || "off").toLowerCase(),
        percent: Number(val.percent) || 0,
        allowlist: Array.isArray(val.allowlist) ? val.allowlist : [],
      };
    }
    
    configCache = {
      dealCardSpecV2_enabled: specEnabled,
      deal_card_v2_rollout: rolloutConfig,
      timestamp: now,
    };
    
    console.log("[RendererRollout] Config loaded:", configCache);
    return configCache;
  } catch (err) {
    console.error("[RendererRollout] Failed to fetch config:", err);
    
    // Return cached or defaults
    return configCache ?? {
      dealCardSpecV2_enabled: false,
      deal_card_v2_rollout: null,
      timestamp: now,
    };
  }
}

/**
 * Clear the config cache (for testing/admin updates).
 */
export function clearRendererConfigCache(): void {
  configCache = null;
}

// ================================================================
// QUERY PARAM DETECTION
// ================================================================

/**
 * Get the dealCardV2 query param override.
 * @returns true = force V2, false = force V1, null = no override
 */
function getQueryParamOverride(): boolean | null {
  if (typeof window === "undefined") return null;
  
  const params = new URLSearchParams(window.location.search);
  const val = params.get("dealCardV2");
  
  if (val === "1" || val === "true") return true;
  if (val === "0" || val === "false") return false;
  return null;
}

// ================================================================
// MAIN DECISION LOGIC
// ================================================================

/**
 * Synchronous decision using cached config.
 * Call fetchRolloutConfigFromDB() first to ensure cache is populated.
 */
export function getDealCardRendererModeSync(options?: {
  manualSelection?: RendererVersion;
}): RendererDecision {
  const { manualSelection } = options ?? {};
  const cache = configCache;
  
  // 1. Query param override (highest priority)
  const queryOverride = getQueryParamOverride();
  if (queryOverride === true) {
    return {
      renderer: "v2",
      reason: "forced_by_query_param",
      isForced: true,
      debugSnapshot: {
        queryOverride: true,
        dealCardSpecV2_enabled: cache?.dealCardSpecV2_enabled,
        deal_card_v2_rollout: cache?.deal_card_v2_rollout 
          ? { enabled: cache.deal_card_v2_rollout.enabled, mode: cache.deal_card_v2_rollout.mode, percent: cache.deal_card_v2_rollout.percent }
          : undefined,
      },
    };
  }
  if (queryOverride === false) {
    return {
      renderer: "v1",
      reason: "forced_by_query_param",
      isForced: true,
      debugSnapshot: {
        queryOverride: false,
        dealCardSpecV2_enabled: cache?.dealCardSpecV2_enabled,
        deal_card_v2_rollout: cache?.deal_card_v2_rollout 
          ? { enabled: cache.deal_card_v2_rollout.enabled, mode: cache.deal_card_v2_rollout.mode, percent: cache.deal_card_v2_rollout.percent }
          : undefined,
      },
    };
  }
  
  // 2. Check rollout config
  const rollout = cache?.deal_card_v2_rollout;
  const specEnabled = cache?.dealCardSpecV2_enabled ?? false;
  
  // V2 is forced ON when:
  // - rollout.enabled = true AND rollout.mode = "on" AND percent >= 100
  // - OR dealCardSpecV2_enabled = true AND rollout.mode = "on"
  const isRolloutFullyOn = 
    rollout?.enabled && 
    rollout.mode === "on" && 
    rollout.percent >= 100;
  
  const isV2ForcedByConfig = 
    (specEnabled && rollout?.mode === "on") ||
    isRolloutFullyOn;
  
  if (isV2ForcedByConfig) {
    return {
      renderer: "v2",
      reason: "forced_by_rollout",
      isForced: true,
      debugSnapshot: {
        queryOverride: null,
        dealCardSpecV2_enabled: specEnabled,
        deal_card_v2_rollout: rollout 
          ? { enabled: rollout.enabled, mode: rollout.mode, percent: rollout.percent }
          : undefined,
      },
    };
  }
  
  // 3. If manual selection provided and not forced, use it
  if (manualSelection) {
    return {
      renderer: manualSelection,
      reason: "manual",
      isForced: false,
      debugSnapshot: {
        queryOverride: null,
        dealCardSpecV2_enabled: specEnabled,
        deal_card_v2_rollout: rollout 
          ? { enabled: rollout.enabled, mode: rollout.mode, percent: rollout.percent }
          : undefined,
      },
    };
  }
  
  // 4. Default to V1
  return {
    renderer: "v1",
    reason: "default",
    isForced: false,
    debugSnapshot: {
      queryOverride: null,
      dealCardSpecV2_enabled: specEnabled,
      deal_card_v2_rollout: rollout 
        ? { enabled: rollout.enabled, mode: rollout.mode, percent: rollout.percent }
        : undefined,
    },
  };
}

/**
 * Async decision that fetches fresh config first.
 */
export async function getDealCardRendererMode(options?: {
  manualSelection?: RendererVersion;
}): Promise<RendererDecision> {
  await fetchRolloutConfigFromDB();
  return getDealCardRendererModeSync(options);
}

/**
 * Check if V2 renderer is forced (no manual override allowed).
 */
export function isV2RendererForced(): boolean {
  const decision = getDealCardRendererModeSync();
  return decision.isForced && decision.renderer === "v2";
}

/**
 * Check if V1 renderer is forced (via query param only).
 */
export function isV1RendererForced(): boolean {
  const decision = getDealCardRendererModeSync();
  return decision.isForced && decision.renderer === "v1";
}

// ================================================================
// REACT HOOK
// ================================================================

export interface UseRendererRolloutResult {
  /** Current renderer decision */
  decision: RendererDecision;
  /** Whether config is still loading */
  loading: boolean;
  /** The effective renderer version to use */
  renderer: RendererVersion;
  /** Whether V2 is forced (toggle should be disabled) */
  isForced: boolean;
  /** Human-readable reason */
  reason: RendererReason;
  /** Force a refresh of the config */
  refresh: () => Promise<void>;
}

/**
 * React hook for renderer rollout decision.
 * 
 * @param manualSelection - User's manual selection (ignored if forced)
 */
export function useRendererRollout(
  manualSelection?: RendererVersion
): UseRendererRolloutResult {
  const [loading, setLoading] = useState(true);
  const [decision, setDecision] = useState<RendererDecision>(() => 
    getDealCardRendererModeSync({ manualSelection })
  );
  
  // Load config on mount
  useEffect(() => {
    let mounted = true;
    
    getDealCardRendererMode({ manualSelection }).then((d) => {
      if (mounted) {
        setDecision(d);
        setLoading(false);
      }
    });
    
    return () => { mounted = false; };
  }, [manualSelection]);
  
  // Refresh function
  const refresh = useCallback(async () => {
    setLoading(true);
    clearRendererConfigCache();
    const d = await getDealCardRendererMode({ manualSelection });
    setDecision(d);
    setLoading(false);
  }, [manualSelection]);
  
  return useMemo(() => ({
    decision,
    loading,
    renderer: decision.renderer,
    isForced: decision.isForced,
    reason: decision.reason,
    refresh,
  }), [decision, loading, refresh]);
}
